import React, { useState, useEffect } from "react";
import axios from "axios";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
} from "recharts";
import { TailSpin } from "react-loader-spinner";
import "./manualTestingStatsV2.css";

const apiUrl = process.env.REACT_APP_API_FilteredOTPCount;

const toMidnightUTC = (date) => {
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
};

const getDateRangeForPreset = (preset) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  let start, end;

  switch (preset) {
    case "today":
      start = new Date(today);
      end = new Date(today);
      break;
    case "yesterday": {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      start = yesterday;
      end = new Date(yesterday);
      break;
    }
    case "thisWeek": {
      const dayOfWeek = today.getDay();
      const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      start = new Date(today);
      start.setDate(today.getDate() + mondayOffset);
      end = new Date(today);
      break;
    }
    case "lastWeek": {
      const dayOfWeek = today.getDay();
      const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      const thisMonday = new Date(today);
      thisMonday.setDate(today.getDate() + mondayOffset);
      start = new Date(thisMonday);
      start.setDate(start.getDate() - 7);
      end = new Date(start);
      end.setDate(end.getDate() + 6);
      break;
    }
    case "thisMonth":
      start = new Date(today.getFullYear(), today.getMonth(), 1);
      end = new Date(today);
      break;
    case "lastMonth":
      start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      end = new Date(today.getFullYear(), today.getMonth(), 0);
      break;
    default:
      start = today;
      end = today;
  }
  return [start, end];
};

const PRESETS = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "thisWeek", label: "This Week" },
  { key: "lastWeek", label: "Last Week" },
  { key: "thisMonth", label: "This Month" },
  { key: "lastMonth", label: "Last Month" },
];

const SpecialTestingStatsV2 = () => {
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 6);
    return d;
  });
  const [endDate, setEndDate] = useState(() => new Date());
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [chartType, setChartType] = useState("bar");
  const [activePreset, setActivePreset] = useState(null);

  const handlePresetClick = (presetKey) => {
    const [start, end] = getDateRangeForPreset(presetKey);
    setStartDate(start);
    setEndDate(end);
    setActivePreset(presetKey);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const start = toMidnightUTC(startDate);
      const end = toMidnightUTC(endDate);

      const response = await axios.post(apiUrl, {
        start_date: start.toISOString().split("T")[0],
        end_date: end.toISOString().split("T")[0],
      });

      const optCount = response.data?.otp_count_with_filter || {};

      const dates = [];
      const current = new Date(start);
      while (current <= end) {
        dates.push(current.toISOString().split("T")[0]);
        current.setDate(current.getDate() + 1);
      }

      const formatted = dates.map((date) => ({
        date,
        count: optCount[date] ?? 0,
      }));

      setChartData(formatted);
    } catch (error) {
      console.error("Error fetching data:", error);
      setChartData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (activePreset !== null) {
      fetchData();
    }
  }, [activePreset]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="manual-stats-v2-tooltip">
        <div className="manual-stats-v2-tooltip-label">{label}</div>
        <div className="manual-stats-v2-tooltip-value">
          {payload[0].value?.toLocaleString()} OTPs
        </div>
      </div>
    );
  };

  const totalOtps = chartData.reduce((sum, d) => sum + (d.count || 0), 0);

  return (
    <div className="manual-stats-v2">
      <div className="manual-stats-v2-header">
        <h1 className="manual-stats-v2-title">
          Total OTPs Generated by the Special Testers Team
        </h1>
        <div className="manual-stats-v2-controls">
          <div className="manual-stats-v2-date-row">
            <DatePicker
              selected={startDate}
              onChange={(d) => {
                setStartDate(d);
                setActivePreset(null);
              }}
              className="manual-stats-v2-datepicker"
              dateFormat="yyyy-MM-dd"
              placeholderText="Start Date"
            />
            <DatePicker
              selected={endDate}
              onChange={(d) => {
                setEndDate(d);
                setActivePreset(null);
              }}
              minDate={startDate}
              className="manual-stats-v2-datepicker"
              dateFormat="yyyy-MM-dd"
              placeholderText="End Date"
            />
            <button
              className="manual-stats-v2-apply"
              onClick={() => {
                setActivePreset(null);
                fetchData();
              }}
            >
              Apply
            </button>
          </div>
          <select
            className="manual-stats-v2-select"
            value={chartType}
            onChange={(e) => setChartType(e.target.value)}
          >
            <option value="bar">Bar Chart</option>
            <option value="line">Line Chart</option>
          </select>
        </div>
      </div>

      <div className="manual-stats-v2-presets">
        {PRESETS.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            className={`manual-stats-v2-preset-btn ${activePreset === key ? "active" : ""}`}
            onClick={() => handlePresetClick(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {totalOtps > 0 && (
        <div className="manual-stats-v2-stat-card">
          <div className="manual-stats-v2-stat-label">Total OTPs (Selected Range)</div>
          <div className="manual-stats-v2-stat-value">
            {totalOtps.toLocaleString()}
          </div>
        </div>
      )}

      <div className="manual-stats-v2-chart-card">
        {loading ? (
          <div className="manual-stats-v2-loading">
            <TailSpin
              height={48}
              width={48}
              color="#38bdf8"
              ariaLabel="loading"
            />
          </div>
        ) : chartData.length > 0 ? (
          <div className="manual-stats-v2-chart-wrapper">
            <ResponsiveContainer width="100%" height={360}>
              {chartType === "line" ? (
                <LineChart
                  data={chartData}
                  margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
                >
                  <defs>
                    <linearGradient
                      id="specialStatsLineGrad"
                      x1="0"
                      y1="0"
                      x2="1"
                      y2="0"
                    >
                      <stop offset="0%" stopColor="#38bdf8" stopOpacity={1} />
                      <stop offset="100%" stopColor="#0ea5e9" stopOpacity={1} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#e2e8f0"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="date"
                    stroke="#64748b"
                    fontSize={11}
                    tickLine={false}
                    axisLine={{ stroke: "#e2e8f0" }}
                  />
                  <YAxis
                    stroke="#64748b"
                    fontSize={11}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v) =>
                      v >= 1000 ? `${v / 1000}k` : v
                    }
                  />
                  <Tooltip
                    content={<CustomTooltip />}
                    cursor={{
                      stroke: "#38bdf8",
                      strokeWidth: 1,
                      strokeDasharray: "4 4",
                    }}
                  />
                  <Legend
                    wrapperStyle={{ fontSize: 12 }}
                    formatter={() => "Total OTPs"}
                  />
                  <Line
                    type="monotone"
                    dataKey="count"
                    stroke="url(#specialStatsLineGrad)"
                    strokeWidth={2.5}
                    dot={{ fill: "#38bdf8", strokeWidth: 0 }}
                    activeDot={{
                      r: 5,
                      fill: "#38bdf8",
                      stroke: "#fff",
                      strokeWidth: 2,
                    }}
                  />
                </LineChart>
              ) : (
                <BarChart
                  data={chartData}
                  margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
                  barRadius={[6, 6, 0, 0]}
                >
                  <defs>
                    <linearGradient
                      id="specialStatsBarGrad"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="0%"
                        stopColor="#38bdf8"
                        stopOpacity={1}
                      />
                      <stop
                        offset="100%"
                        stopColor="#0ea5e9"
                        stopOpacity={0.9}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#e2e8f0"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="date"
                    stroke="#64748b"
                    fontSize={11}
                    tickLine={false}
                    axisLine={{ stroke: "#e2e8f0" }}
                  />
                  <YAxis
                    stroke="#64748b"
                    fontSize={11}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v) =>
                      v >= 1000 ? `${v / 1000}k` : v
                    }
                  />
                  <Tooltip
                    content={<CustomTooltip />}
                    cursor={{ fill: "rgba(56, 189, 248, 0.08)" }}
                  />
                  <Legend
                    wrapperStyle={{ fontSize: 12 }}
                    formatter={() => "Total OTPs"}
                  />
                  <Bar
                    dataKey="count"
                    fill="url(#specialStatsBarGrad)"
                    name="Total OTPs"
                    radius={[6, 6, 0, 0]}
                  />
                </BarChart>
              )}
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="manual-stats-v2-empty">
            No data for the selected date range. Try a different range.
          </div>
        )}
      </div>
    </div>
  );
};

export default SpecialTestingStatsV2;
